import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const SetupTwoFactor = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    API.get('/auth/setup-2fa')
      .then(res => {
        setQrCode(res.data.qrCode);
        setSecret(res.data.secret);
      })
      .catch(() => setError('Could not load 2FA setup. Try refreshing.'))
      .finally(() => setLoading(false));
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);
    try {
      await API.post('/auth/enable-2fa', { code });
      await checkAuth();
      toast.success('Two-factor authentication enabled');
      navigate('/select-customer');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code — try again');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="page-container" style={{ alignItems: 'flex-start', paddingTop: '40px' }}>
      <div className="card" style={{ maxWidth: '520px' }}>
        <h1>Set Up Two-Factor Authentication</h1>
        <p>For security, every account must use two-factor authentication. This is a one-time setup.</p>

        <div style={{ background: '#F4F7FB', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>Steps:</p>
          <ol style={{ fontSize: '13px', color: '#555', paddingLeft: '18px', lineHeight: '1.7' }}>
            <li>Install <strong>Microsoft Authenticator</strong> on your phone (free, from your app store)</li>
            <li>Open it and tap <strong>+</strong> → <strong>Other account</strong></li>
            <li>Scan the QR code below (or enter the key manually)</li>
            <li>Enter the 6-digit code it shows to finish</li>
          </ol>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <p style={{ color: '#5B6B7F' }}>Loading setup...</p>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              {qrCode && (
                <img
                  src={qrCode}
                  alt="2FA QR code"
                  style={{ width: '200px', height: '200px', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                />
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '12px', color: '#5B6B7F', marginBottom: '4px' }}>Can't scan? Enter this key manually:</p>
              <div style={{ background: '#F4F7FB', borderRadius: '6px', padding: '10px', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all', textAlign: 'center' }}>
                {secret}
              </div>
            </div>

            <form onSubmit={handleVerify}>
              <div className="form-group">
                <label>Enter the 6-digit code from the app</label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  required
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={verifying}>
                {verifying ? 'Verifying...' : 'Enable Two-Factor Authentication'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SetupTwoFactor;